import { useEffect, useState, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Col,
  Row,
  Progress,
  Label,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import useAuth from 'src/hooks/useAuth';
import css from 'src/utils/css';
import Button from 'src/componentes/button';
import { capitalizeFirstLetter } from 'src/utils/string';
import LucideIcon from 'src/componentes/lucide-icon';
import Checkbox from 'src/componentes/checkbox';
import Input from 'src/componentes/input';
import DatePicker from 'src/componentes/date-picker';
import ResponsavelButtonLookup from 'src/lookups/colaborador-button';
import ResponsavelLookup from 'src/lookups/colaborador';
import TagLookup from 'src/lookups/TagLookup';
import Avatar from 'src/componentes/avatar';
import formatarData from 'src/utils/formatarData';
import ContentManager from 'src/componentes/content-manager';
import NovoProjeto from 'src/paginas/item/novo';
import SchemaProjeto from 'src/paginas/projeto/schema';
import { TIPO_ITENS, MODELO_ITEM } from 'src/utils/constants';
import Confirm from 'src/componentes/message-box/confirm';
import {
  renderTarefasIniciativaInlineHeader,
  renderTarefasIniciativaInlineItem,
  getResolvedTarefasIniciativaSalvarItemLabelKey,
} from 'src/componentes/checklist/InlineTarefasIniciativas';
import Badge from 'src/componentes/Badge';
import Dialog from 'src/componentes/dialog';

const classes = css`
  .hoverEffect {
    padding: 2px;
    &:hover {
      background: #f3f3f3;
    }
  }
  .background {
    padding: 2px;
    background: #f3f3f3;
  }
  .removeHover {
    transition: all ease-in-out 0.1s;
    &:hover {
      cursor: pointer;
      color: #f05050 !important;
    }
  }
`;

const novaIniciativaEsqueleto = {
  descricao: null,
  responsavel: null,
  dataConclusao: null,
  concluida: false,
  tarefas: [],
  isNew: true,
};

const Checklist = ({
  itens,
  disabled,
  setItens,
  showResponsavel = true,
  showDate = true,
  showFieldLabel = true,
  labelKey = ' ',
  descriptionPlaceHolder = ' ',
  descriptionRows = 2,
  permissions = null,
  showTags = false,
  okrInlineLayout = false,
  KR = null,
  acaoId,
  salvarItemLabelKey,
  renderOkrInlineHeader = null,
  renderOkrInlineItem = null,
}) => {
  const intl = useIntl();
  const { terms: resources, user } = useAuth();

  let iniciativaRef = useRef();
  const usuarioAbriuAdicionarRef = useRef(false);
  const itensRef = useRef(itens);
  itensRef.current = itens;

  const [adicionarNovo, setAdicionarNovo] = useState(
    () => okrInlineLayout && !itens?.length && !disabled
  );
  const [novaIniciativa, setNovaIniciativa] = useState(novaIniciativaEsqueleto);
  const [iniciativaEdicao, setIniciativaEdicao] = useState(null);
  const [rascunhos, setRascunhos] = useState([]);
  const [responsavelLookup, setResponsavelLookup] = useState(false);
  const [showTarefasModal, setShowTarefasModal] = useState(false);
  const [currentIniciativaForTarefas, setCurrentIniciativaForTarefas] =
    useState(null);
  const [tarefasNoModal, setTarefasNoModal] = useState([]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleClickOutside = (event) => {
    var elements1 = document.getElementsByClassName('rdtPicker');
    var elements2 = document.getElementsByClassName('popupOpen');

    var array1 = [].slice.call(elements1);
    var array2 = [].slice.call(elements2);

    let ref = iniciativaRef && iniciativaRef.node;
    let isNew = iniciativaRef && iniciativaRef.isNew;

    if (ref && !ref.contains(event.target)) {
      var validacao = false;

      if (array1 && array1.length > 0) {
        array1.forEach((element) => {
          if (element && element.contains(event.target)) {
            validacao = true;
          }
        });
      }

      if (!validacao && array2 && array2.length > 0) {
        array2.forEach((element) => {
          if (element && element.contains(event.target)) {
            validacao = true;
          }
        });
      }

      let nomeClasse = event.target.className;

      if (!validacao) {
        if (nomeClasse.includes('Select-option')) return;

        if (isNew) {
          if (itensRef.current?.length) {
            setAdicionarNovo(false);
            setResponsavelLookup(false);
          } else {
            setNovaIniciativa(novaIniciativaEsqueleto);
            setResponsavelLookup(false);
          }
        } else {
          setIniciativaEdicao(null);
        }
      }
    }
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(itens, result.source.index, result.destination.index);

    setItens(items);
  };

  const handleChangeIniciativaRascunho = (field, id, value) => {
    let rascunhosObj = Object.assign([], rascunhos);
    let rascunho = rascunhosObj.find((r) => r.id === id);

    if (rascunho != null) {
      rascunho[field] = value;
      rascunho['alterado'] = true;
    }

    setRascunhos(rascunhosObj);
  };

  const handleChangeNovaIniciativa = (field, value) => {
    const item = {
      ...novaIniciativa,
      [field]: value,
    };

    setNovaIniciativa(item);
  };

  const abrirAdicionar = () => {
    usuarioAbriuAdicionarRef.current = true;
    setAdicionarNovo(true);
  };

  const closeNew = () => {
    setNovaIniciativa(novaIniciativaEsqueleto);
    setResponsavelLookup(false);
    usuarioAbriuAdicionarRef.current = false;

    if (!itens?.length) {
      setAdicionarNovo(true);
    } else {
      setAdicionarNovo(false);
    }
  };

  const removeRascunho = (id) => {
    let rascunhosObj = Object.assign([], rascunhos);
    let index = rascunhosObj.findIndex((r) => r.id == id);

    if (index != -1) {
      rascunhosObj.splice(index, 1);
    }

    return rascunhosObj;
  };

  const close = (id) => {
    setIniciativaEdicao(null);
    setRascunhos(removeRascunho(id));
    setResponsavelLookup(false);
  };

  const verificaIdIniciativa = (id) => {
    return itens && itens.find((t) => t.id == id) == null;
  };

  const retornaRascunho = (id) => {
    return rascunhos.find((r) => r.id == id);
  };

  const criarOuRetornarRascunho = (item) => {
    let rascunho = retornaRascunho(item.id);

    if (rascunho != null) return rascunho;
    else {
      let novoRascunho = { ...item, alterado: false };
      let rascunhosObj = Object.assign([], rascunhos);

      rascunhosObj.push(novoRascunho);

      setRascunhos(rascunhosObj);

      return novoRascunho;
    }
  };

  const handleSalvar = (isNew, item) => {
    let iniciativasObj = Object.assign([], itens || []);
    if (isNew) {
      let iniciativaAdd = Object.assign({}, novaIniciativa);

      //identificador temporário para o rascunho (tem que ser int)
      let idValido = false;
      let id;

      do {
        id = Math.floor(Math.random() * 10000) + 1;
        idValido = verificaIdIniciativa(id);
      } while (!idValido);

      iniciativaAdd.id = id;
      iniciativasObj.push(iniciativaAdd);

      setItens(iniciativasObj);
      closeNew();
    } else {
      let index = iniciativasObj.findIndex((a) => a.id == item.id);
      if (iniciativasObj && index != -1 && iniciativasObj[index] != null) {
        //mantendo a conclusão
        const rascunho = criarOuRetornarRascunho(item);
        iniciativasObj[index] = {
          ...rascunho,
          concluida: item.concluida,
          isNew: rascunho.isNew ?? false,
        };

        setIniciativaEdicao(null);
        setRascunhos(removeRascunho(item.id));

        setItens(iniciativasObj);
      }
    }
  };

  const checkIniciativa = (index) => {
    let iniciativasObj = Object.assign([], itens || []);

    if (iniciativasObj && iniciativasObj[index] != null) {
      iniciativasObj[index].concluida = !iniciativasObj[index].concluida;
      setItens(iniciativasObj);
    }
  };

  const podeSalvar = (descricao) => {
    if (descricao == null || descricao.length < 1) return false;

    return true;
  };

  const calculaCorDataConclusao = (data) => {
    let dataHoje = new Date();
    dataHoje.setHours(0, 0, 0, 0);

    if (data < dataHoje) return '#f05050';
  };

  const handleRemoveIniciativa = (id) => {
    let iniciativasObj = Object.assign([], itens || []);

    if (iniciativasObj) {
      let index = iniciativasObj.findIndex((t) => t.id == id);

      if (index != -1 && iniciativasObj[index] != null) {
        removeRascunho(iniciativasObj[index].id);
        iniciativasObj.splice(index, 1);
        setItens(iniciativasObj);

        if (!iniciativasObj.length) {
          usuarioAbriuAdicionarRef.current = false;
          setAdicionarNovo(true);
        }
      }
    }
  };

  const handleAtivarEdicao = (item) => {
    criarOuRetornarRascunho(item);

    setIniciativaEdicao(item);
  };

  const handleTransformarProjeto = (iniciativa) => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'msgConfirmacaoConversaoIniciativaProjeto',
        })}
        handleConfirm={() => {
          ContentManager.addContent(
            <NovoProjeto
              tipoItem={TIPO_ITENS.PROJETO}
              url={'/Projeto'}
              schema={SchemaProjeto}
              modeloItem={{ id: MODELO_ITEM.INDIVIDUAL }}
              parameters={{
                KR: KR,
                iniciativa: iniciativa,
                isOrigemIniciativa: true,
              }}
            />
          );
        }}
      />
    );
  };

  const handleAbrirTarefasModal = (iniciativa) => {
    if (!iniciativa || !iniciativa.id) return;

    const rascunho = retornaRascunho(iniciativa.id);
    const tarefasAtuais = rascunho?.tarefas || iniciativa.tarefas || [];

    setCurrentIniciativaForTarefas(iniciativa);
    setTarefasNoModal(tarefasAtuais);
    setShowTarefasModal(true);
  };

  const salvarTarefasModal = () => {
    if (!currentIniciativaForTarefas) return;

    const id = currentIniciativaForTarefas.id;
    let rascunho = retornaRascunho(id);

    if (!rascunho) {
      criarOuRetornarRascunho(currentIniciativaForTarefas);
    }

    if (rascunho) {
      handleChangeIniciativaRascunho('tarefas', id, tarefasNoModal);
    } else {
      let rascunhosObj = Object.assign([], rascunhos);
      const novoRascunho = {
        ...currentIniciativaForTarefas,
        tarefas: tarefasNoModal,
        alterado: true,
      };
      rascunhosObj.push(novoRascunho);
      setRascunhos(rascunhosObj);
    }

    let iniciativasObj = Object.assign([], itens || []);
    const index = iniciativasObj.findIndex((a) => a.id == id);

    if (index !== -1) {
      iniciativasObj[index] = {
        ...iniciativasObj[index],
        tarefas: tarefasNoModal,
      };
      setItens(iniciativasObj);
    }

    setShowTarefasModal(false);
    setCurrentIniciativaForTarefas(null);
    setTarefasNoModal([]);
  };

  const fecharTarefasModal = () => {
    setShowTarefasModal(false);
    setCurrentIniciativaForTarefas(null);
    setTarefasNoModal([]);
  };

  const renderTags = (tags) => {
    return tags?.map((x) => (
      <Badge
        key={x.id}
        pill
        className="text-white"
        style={{ background: x.cor }}
      >
        {x.descricao}
      </Badge>
    ));
  };

  const retornaRascunhoAlterado = (id) => {
    return rascunhos.find((r) => r.id == id && r.alterado == true);
  };

  const renderIniciativa = (
    item,
    index,
    isDragging,
    dragHandlePropsForOkr = null
  ) => {
    if (okrInlineLayout && typeof renderOkrInlineItem === 'function') {
      return renderOkrInlineItem({
        item,
        index,
        isDragging,
        dragHandleProps: dragHandlePropsForOkr,
        intl,
        resources,
        user,
        disabled,
        permissions,
        actions: {
          checkIniciativa,
          handleAtivarEdicao,
          handleRemoveIniciativa,
          handleTransformarProjeto,
          close,
          handleAbrirTarefasModal,
        },
        utils: {
          calculaCorDataConclusao,
          retornaRascunhoAlterado,
        },
      });
    }

    return (
      <div
        className={clsx('p-2', classes.hoverEffect, {
          [classes.background]: isDragging,
        })}
        style={{ width: '100%' }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              disabled={disabled}
              height={17}
              width={17}
              style={{ marginRight: 8 }}
              model={{
                value: item.concluida,
                requestChange: () => checkIniciativa(index),
              }}
            />

            <div
              style={{
                textAlign: 'left',
                flex: 1,
                textDecoration: item.concluida ? 'line-through' : 'none',
                fontWeight: 500,
              }}
              onClick={() => handleAtivarEdicao(item)}
            >
              {item.descricao}
            </div>
            <div>
              <UncontrolledDropdown group>
                <DropdownToggle tag="a" className="nav-link p-0">
                  <LucideIcon icon="EllipsisVertical" size={18} />
                </DropdownToggle>
                <DropdownMenu
                  className="mr-3"
                  placement="right"
                  container="body"
                >
                  {
                    <DropdownItem
                      eventkey="edit"
                      onClick={() => handleTransformarProjeto(item)}
                    >
                      <LucideIcon className="mr-2" icon="wrap" />
                      {capitalizeFirstLetter(
                        intl.formatMessage(
                          { id: 'transformarEmProjeto' },
                          { projeto: resources.projeto }
                        )
                      )}
                    </DropdownItem>
                  }
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 6,
              paddingLeft: 25,
            }}
            onClick={() => handleAtivarEdicao(item)}
          >
            {item.responsavel != null && (
              <div
                id={`id${item.responsavel?.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 8,
                }}
              >
                <Avatar
                  foto={item.responsavel?.fotoArquivo}
                  showLabel={false}
                  border={1}
                  size={28}
                  label={item.responsavel?.nome}
                />
              </div>
            )}

            <div
              style={{
                marginRight: 10,
                fontSize: 13,
                fontWeight: 500,
                color:
                  !item.concluida &&
                  calculaCorDataConclusao(item.dataConclusao),
                textDecoration: item.concluida ? 'line-through' : 'none',
              }}
            >
              {item.dataConclusao &&
                formatarData(user.idioma, item.dataConclusao)}
            </div>
            {item && item.tags && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                }}
              >
                {renderTags(item.tags)}
              </div>
            )}
          </div>
        </div>

        {retornaRascunhoAlterado(item.id) != null && (
          <div style={{ display: 'flex', marginTop: 5 }}>
            <span>
              {intl.formatMessage({
                id: 'voceTemEdicoesNaoSalvasNesteCampo',
              })}
            </span>
            <a
              className="text-primary"
              tabIndex="-1"
              onClick={() => handleAtivarEdicao(item)}
              style={{ marginLeft: 5 }}
            >
              {intl.formatMessage({ id: 'exibirEdicao' })}
            </a>
            <a
              className="text-primary"
              tabIndex="-1"
              onClick={() => close(item.id)}
              style={{ marginLeft: 5 }}
            >
              {intl.formatMessage({ id: 'descartar' })}
            </a>
          </div>
        )}
      </div>
    );
  };

  const handleChangeLookup = (lookupReponsavel) => {
    setResponsavelLookup(lookupReponsavel);
  };

  const renderIniciativaEdicao = (isNew, iniciativaParam = null) => {
    let item;

    if (!isNew) {
      let rascunho = criarOuRetornarRascunho(iniciativaParam);
      item = rascunho;
    } else {
      item = novaIniciativa;
    }

    const tamanhoColuna = showTags ? 3 : 4;

    const getFieldRequestChange = (field) =>
      !isNew
        ? (value) => handleChangeIniciativaRascunho(field, item.id, value)
        : (value) => handleChangeNovaIniciativa(field, value);

    const descricaoLabel = showFieldLabel ? (
      <Label>{intl.formatMessage({ id: 'descricao' })} *</Label>
    ) : null;

    const renderDescricaoInput = (rows) => (
      <Input
        placeholder={capitalizeFirstLetter(
          intl.formatMessage({ id: descriptionPlaceHolder })
        )}
        disabled={disabled}
        rows={rows}
        required
        style={{ marginBottom: 0 }}
        type="textarea"
        model={{
          value: item.descricao,
          requestChange: getFieldRequestChange('descricao'),
        }}
      />
    );

    const renderResponsavelInline = () => (
      <ResponsavelLookup
        disabled={disabled}
        fromAcao
        acaoId={acaoId}
        tipoLookup="acao"
        clearable
        formGroupStyle={{ marginBottom: 0 }}
        model={{
          label: showFieldLabel
            ? capitalizeFirstLetter(resources.responsavel)
            : '',
          value: item.responsavel,
          requestChange: getFieldRequestChange('responsavel'),
        }}
      />
    );

    const renderResponsavelPadrao = () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px 5px',
        }}
      >
        {showFieldLabel && (
          <Label className="control-label">
            {capitalizeFirstLetter(resources.responsavel)}
          </Label>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ResponsavelButtonLookup
            disabled={disabled}
            showLabel={false}
            compact
            callbackLookup={handleChangeLookup}
            fromAcao
            acaoId={acaoId}
            tipoLookup="acao"
            remove={() => getFieldRequestChange('responsavel')(null)}
            formGroupStyle={{ marginBottom: 0 }}
            multi={false}
            model={{
              label: '',
              value: item.responsavel,
              requestChange: getFieldRequestChange('responsavel'),
            }}
          />
          {item?.responsavel?.nome ? (
            <div
              style={{
                color: '#09090B',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <span>{item.responsavel.nome}</span>
            </div>
          ) : null}
        </div>
      </div>
    );

    const renderDataPicker = () => (
      <DatePicker
        style={{ height: 38 }}
        disabled={disabled}
        timeFormat={false}
        model={{
          label: showFieldLabel ? intl.formatMessage({ id: 'data' }) : '',
          value: item.dataConclusao,
          requestChange: getFieldRequestChange('dataConclusao'),
        }}
      />
    );

    const renderTagsLookup = () => (
      <TagLookup
        multi
        model={{
          label: showFieldLabel
            ? capitalizeFirstLetter(
                intl.formatMessage({ id: 'tags' }, { tags: resources.tags })
              )
            : '',
          value: item.tags,
          requestChange: getFieldRequestChange('tags'),
        }}
        data={{ funcionalidadesIdSelecionaveis: [13] }}
        podeEditar={false}
      />
    );

    const camposEdicao = okrInlineLayout ? (
      <Row>
        <Col md={tamanhoColuna}>
          {descricaoLabel}
          {renderDescricaoInput(1)}
        </Col>
        {showResponsavel && (
          <Col md={tamanhoColuna}>{renderResponsavelInline()}</Col>
        )}
        {showDate && <Col md={tamanhoColuna}>{renderDataPicker()}</Col>}
        {showTags && <Col md={tamanhoColuna}>{renderTagsLookup()}</Col>}
      </Row>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex' }}>
          <div style={{ textAlign: 'left', flex: 1 }}>
            {descricaoLabel}
            {renderDescricaoInput(descriptionRows)}
          </div>
        </div>
        <div style={{ display: 'flex', marginTop: 10 }}>
          {showResponsavel && (
            <div
              style={{
                textAlign: 'left',
                flexGrow: responsavelLookup ? 3 : 1,
                flexBasis: 0,
                margin: '0 10px',
              }}
            >
              {renderResponsavelPadrao()}
            </div>
          )}
          {showDate && (
            <div style={{ textAlign: 'left', flexGrow: 2, flexBasis: 0 }}>
              {renderDataPicker()}
            </div>
          )}
          <div
            style={{
              textAlign: 'left',
              flexGrow: 2,
              flexBasis: 0,
              marginLeft: 10,
            }}
          >
            {showTags && renderTagsLookup()}
          </div>
        </div>
      </div>
    );

    return (
      <div
        ref={(node) => (iniciativaRef = { isNew: isNew, node: node })}
        className={`p-2 ${classes.hoverEffect}`}
      >
        {camposEdicao}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
            marginTop: 8,
          }}
        >
          {okrInlineLayout ? (
            <>
              <a
                tabIndex={0}
                style={{
                  color: '#71717A',
                  textDecoration: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
                onClick={() => {
                  if (!disabled) {
                    isNew ? closeNew() : close(item.id);
                  }
                }}
              >
                <FormattedMessage id="label.cancelar" />
              </a>
              {!isNew && (!permissions || permissions.allowDelete) && (
                <Button
                  disabled={disabled}
                  color="danger"
                  onClick={() => handleRemoveIniciativa(item.id)}
                >
                  <FormattedMessage id="excluir" />
                </Button>
              )}
              <a
                className="text-primary"
                tabIndex={0}
                style={{
                  textDecoration: 'none',
                  cursor:
                    !podeSalvar(item.descricao) ||
                    disabled ||
                    (permissions != null && !permissions.allowEdit)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !podeSalvar(item.descricao) ||
                    disabled ||
                    (permissions != null && !permissions.allowEdit)
                      ? 0.5
                      : 1,
                }}
                onClick={() => {
                  if (
                    podeSalvar(item.descricao) &&
                    !disabled &&
                    (permissions == null || permissions.allowEdit)
                  ) {
                    handleSalvar(isNew, iniciativaParam);
                  }
                }}
              >
                {salvarItemLabelKey
                  ? `${capitalizeFirstLetter(
                      intl.formatMessage({ id: 'label.salvar' })
                    )} ${intl.formatMessage({ id: salvarItemLabelKey })}`
                  : intl.formatMessage({ id: 'label.salvar' })}
              </a>
            </>
          ) : (
            <>
              <Button
                disabled={disabled}
                key={1}
                onClick={() => (isNew ? closeNew() : close(item.id))}
                color="secondary"
              >
                <FormattedMessage id="label.cancelar" />
              </Button>
              {!isNew && (!permissions || permissions.allowDelete) && (
                <Button
                  disabled={disabled}
                  className="ml-2"
                  color="danger"
                  onClick={() => handleRemoveIniciativa(item.id)}
                >
                  <FormattedMessage id="excluir" />
                </Button>
              )}
              <Button
                disabled={
                  !podeSalvar(item.descricao) ||
                  disabled ||
                  (permissions != null && !permissions.allowEdit)
                }
                key={2}
                type="primary"
                onClick={() => handleSalvar(isNew, iniciativaParam)}
                htmlType="submit"
                className="ml-2"
              >
                <FormattedMessage id="label.salvar" />
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const calculaValorProgresso = (itens) => {
    if (itens && itens.length > 0) {
      let value = Math.floor(
        (100 * itens.filter((a) => a.concluida == true).length) / itens.length
      );
      return value + '';
    }

    return 0;
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!okrInlineLayout || disabled) return;

    if (!itens?.length) {
      setAdicionarNovo(true);
      usuarioAbriuAdicionarRef.current = false;
    } else if (!usuarioAbriuAdicionarRef.current) {
      setAdicionarNovo(false);
    }
  }, [itens?.length, disabled, okrInlineLayout]);

  let value = calculaValorProgresso(itens);

  return (
    <>
      {okrInlineLayout ? (
        typeof renderOkrInlineHeader === 'function' &&
        renderOkrInlineHeader({
          itens,
          value,
          intl,
          resources,
          labelKey,
          disabled,
          adicionarNovo,
          onAdd: abrirAdicionar,
        })
      ) : (
        <div
          className="mb-2"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex' }}>
            <span>
              {capitalizeFirstLetter(intl.formatMessage({ id: labelKey }))}
            </span>
            <span style={{ marginLeft: 10 }}>
              {itens && itens.filter((a) => a.concluida == true).length}/
              {itens && itens.length}
            </span>
          </div>
          <div style={{ display: 'flex', marginTop: -3 }}>
            <span style={{ margin: '0 5px' }}>{value}%</span>
            <Progress
              style={{ width: 75, height: 8, marginTop: 6 }}
              value={value}
            />
          </div>
          <Button
            color="secondary"
            disabled={disabled}
            style={{
              display: 'flex',
              height: 35,
              width: 40,
              justifyContent: 'center',
            }}
            className="ml-2"
            onClick={abrirAdicionar}
          >
            <LucideIcon icon="plus-circle-outline" />
          </Button>
        </div>
      )}
      <div style={{ width: '100%' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Row>
            <Col md={12} style={{ textAlign: 'center' }}>
              <div className={classes.config}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div ref={provided.innerRef}>
                      {itens &&
                        itens.map((item, index) =>
                          iniciativaEdicao != null &&
                          iniciativaEdicao.id == item.id ? (
                            renderIniciativaEdicao(false, item)
                          ) : (
                            <Draggable
                              index={index}
                              key={index + ''}
                              draggableId={index + ''}
                            >
                              {(provided, snapshot) => {
                                const rowStyle = {
                                  ...provided.draggableProps.style,
                                };
                                const hasCustomOkrItem =
                                  okrInlineLayout &&
                                  typeof renderOkrInlineItem === 'function';

                                return (
                                  <div>
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...(hasCustomOkrItem
                                        ? {}
                                        : provided.dragHandleProps)}
                                      style={rowStyle}
                                    >
                                      {renderIniciativa(
                                        item,
                                        index,
                                        snapshot.isDragging,
                                        hasCustomOkrItem
                                          ? provided.dragHandleProps
                                          : null
                                      )}
                                    </div>
                                    {provided.placeholder}
                                  </div>
                                );
                              }}
                            </Draggable>
                          )
                        )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </Col>
          </Row>
        </DragDropContext>
        {adicionarNovo && renderIniciativaEdicao(true)}
      </div>
      {showTarefasModal && currentIniciativaForTarefas && (
        <Dialog
          title={capitalizeFirstLetter(
            intl.formatMessage({ id: 'labelTarefasIniciativa' })
          )}
          padContent
          actions={[
            <Button key="cancel" color="secondary" onClick={fecharTarefasModal}>
              <FormattedMessage id="label.cancelar" />
            </Button>,
            <Button key="save" color="primary" onClick={salvarTarefasModal}>
              <FormattedMessage id="label.salvar" />
            </Button>,
          ]}
          onRequestClose={fecharTarefasModal}
          minWidth={680}
          minHeight={450}
          styleTitle={{
            color: '#09090B',
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          <div style={{ marginBottom: 20 }}>
            <div
              className="text-primary mt-1 mb-3"
              style={{ fontSize: 16, fontWeight: 700 }}
            >
              {currentIniciativaForTarefas.descricao}
            </div>

            {(() => {
              const tarefas = tarefasNoModal || [];
              const totalTarefas = tarefas.length;
              const concluidas = tarefas.filter(
                (t) => t.concluida === true
              ).length;
              const progressoTarefas =
                totalTarefas > 0
                  ? Math.floor((100 * concluidas) / totalTarefas)
                  : 0;

              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    paddingBottom: 12,
                    borderBottom: '1px solid #E4E4E7',
                  }}
                >
                  <Badge
                    pill
                    color="secondary"
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      padding: '5px 12px',
                    }}
                  >
                    {concluidas}/{totalTarefas}
                  </Badge>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flex: 1,
                      maxWidth: 320,
                    }}
                  >
                    <span
                      style={{
                        color: '#71717A',
                        fontSize: 13,
                        fontWeight: 500,
                        minWidth: 32,
                      }}
                    >
                      {progressoTarefas}%
                    </span>
                    <Progress
                      style={{ flex: 1, height: 8, marginTop: 0 }}
                      value={progressoTarefas}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          <Checklist
            okrInlineLayout
            renderOkrInlineHeader={renderTarefasIniciativaInlineHeader}
            renderOkrInlineItem={renderTarefasIniciativaInlineItem}
            disabled={disabled}
            itens={tarefasNoModal}
            setItens={setTarefasNoModal}
            salvarItemLabelKey={getResolvedTarefasIniciativaSalvarItemLabelKey(
              'tarefa',
              'tarefa'
            )}
            showTags={false}
            permissions={permissions}
            isListaTarefasIniciativa
          />
        </Dialog>
      )}
    </>
  );
};

export default Checklist;
